package dev.kuml.portal.web.components

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.html.button
import io.kvision.html.div
import kotlinx.coroutines.launch

/**
 * R-3: the "please verify your email address" banner shown at the top of
 * [dev.kuml.portal.web.projects.projectListScreen] for a caller whose account has
 * `UserResponse.emailVerified == false` -- the missing piece that used to leave every
 * `POST /api/auth/verification/resend` call unreachable from the UI (nothing else in this frontend
 * calls [PortalApi.resendVerification]).
 *
 * Fetches its OWN `GET /api/auth/me` on mount rather than accepting the already-loaded
 * [dev.kuml.portal.web.api.UserResponse] as a parameter -- mirrors
 * [dev.kuml.portal.web.components.portalHeader]'s `loadIdentity`: a self-contained, silently-failing
 * informational load, same discipline as `usageChip`/`activitySection`. A network hiccup here just
 * means the banner stays absent rather than blocking the rest of the screen; it is purely advisory
 * (every write path remains reachable while unverified except team invitations, see
 * `dev.kuml.portal.server.teams.teamRoutes`' `email_not_verified` gate).
 */
public fun Container.emailVerificationBanner(api: PortalApi) {
    val host = div()

    fun render() {
        host.removeAll()
        host.div(className = "kportal-verify-banner") {
            div(t.auth.verifyBannerMessage, className = "kportal-verify-banner-text")
            div(className = "kportal-verify-banner-actions") {
                val statusSpan = div(className = "kportal-status-neutral")
                val resendButton =
                    button(t.auth.verifyBannerResend, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
                resendButton.onClick {
                    resendButton.disabled = true
                    resendButton.text = t.auth.verifyBannerResendBusy
                    AppScope.launch {
                        val result = api.resendVerification()
                        resendButton.disabled = false
                        resendButton.text = t.auth.verifyBannerResend
                        when (result) {
                            is ApiResult.Success -> statusSpan.content = t.auth.verifyBannerResendSuccess
                            is ApiResult.HttpError ->
                                statusSpan.content =
                                    if (result.status == 429) {
                                        t.errors.rateLimited(result.retryAfterSeconds)
                                    } else {
                                        t.errors.forCode(result.error?.code, result.status) ?: t.common.networkErrorRetry
                                    }
                            ApiResult.NetworkError -> statusSpan.content = t.common.networkErrorRetry
                        }
                    }
                }
            }
        }
    }

    AppScope.launch {
        val result = api.me()
        if (result is ApiResult.Success && !result.value.emailVerified) {
            render()
        }
    }
}
